import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Plus, Pencil, Trash, FileText, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  createPage,
  deletePage,
  listPages,
  PAGE_STATUS_META,
} from "@/lib/cms/pages";
import {
  RECOMMENDED_PAGE_TEMPLATES,
  buildPageFromTemplate,
} from "@/lib/cms/recommendedPageTemplates";

const STATUS_TABS = [
  { value: "",          label: "All" },
  { value: "draft",     label: "Drafts" },
  { value: "published", label: "Published" },
  { value: "archived",  label: "Archived" },
];

export default function CmsPages() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [creatingKey, setCreatingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listPages({ status: status || null });
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load pages");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.title || "").toLowerCase().includes(q) ||
        (r.slug || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    drafts: rows.filter((r) => r.status === "draft").length,
    published: rows.filter((r) => r.status === "published").length,
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await deletePage(p.id);
      toast.success(`Deleted ${p.title}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  // Module E · one-click recommended page templates. We hide a template
  // once the admin has already created a page with that exact slug —
  // no point nagging.
  const existingSlugs = useMemo(
    () => new Set(rows.map((r) => (r.slug || "").toLowerCase())),
    [rows],
  );
  const availableTemplates = RECOMMENDED_PAGE_TEMPLATES.filter(
    (t) => !existingSlugs.has(t.slug.toLowerCase()),
  );

  const onCreateFromTemplate = async (template) => {
    if (!profile?.organization_id) {
      toast.error("Could not detect organization");
      return;
    }
    setCreatingKey(template.key);
    try {
      const payload = buildPageFromTemplate(template);
      const created = await createPage({
        organizationId: profile.organization_id,
        ...payload,
      });
      toast.success(`Created "${template.title}" as a draft — open to edit`);
      await load();
      return created;
    } catch (err) {
      toast.error(err.message || `Could not create "${template.title}"`);
    } finally {
      setCreatingKey(null);
    }
  };

  return (
    <div data-testid="cms-pages" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Content
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Pages
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Long-form content — handbook, welcome page, policies. Pages move
              through draft → published → archived.
            </p>
          </div>
          <Link
            to="/cms/pages/new"
            data-testid="cms-pages-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <Plus size={14} weight="bold" />
            New page
          </Link>
        </div>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <Kpi testId="cms-pages-kpi-total" label="Pages" value={String(kpis.total)} />
        <Kpi testId="cms-pages-kpi-drafts" label="Drafts" value={String(kpis.drafts)} />
        <Kpi testId="cms-pages-kpi-published" label="Published" value={String(kpis.published)} accent />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <input
            data-testid="cms-pages-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or slug"
            className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              data-testid={`cms-pages-filter-${t.value || "all"}`}
              onClick={() => setStatus(t.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                status === t.value
                  ? "border-primary/40 bg-primary text-white"
                  : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {availableTemplates.length > 0 && (
        <section
          data-testid="cms-pages-recommended"
          className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-5"
        >
          <header className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-primary-hover shadow-sm">
              <Sparkle size={16} weight="duotone" />
            </span>
            <div>
              <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                Recommended pages
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-neutral-600">
                One-click templates with calm starter copy. Each comes in as a draft so you can edit before publishing.
              </p>
            </div>
          </header>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {availableTemplates.map((t) => (
              <li key={t.key}>
                <button
                  type="button"
                  data-testid={`cms-pages-template-${t.key}`}
                  onClick={() => onCreateFromTemplate(t)}
                  disabled={creatingKey !== null}
                  className="flex w-full items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3 text-left transition hover:border-primary/40 hover:bg-primary/[0.02] disabled:opacity-60"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30 text-primary-hover">
                    <Plus size={14} weight="bold" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-heading text-sm font-semibold text-neutral-900">
                      {t.title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-neutral-500">
                      {t.description}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyCard testId="cms-pages-empty" title="No pages yet" desc='Create your first page — try a "Welcome" or "Policies" page.' />
      ) : (
        <ul data-testid="cms-pages-list" className="space-y-2">
          {filtered.map((p) => {
            const meta = PAGE_STATUS_META[p.status] || PAGE_STATUS_META.draft;
            return (
              <li
                key={p.id}
                data-testid={`cms-pages-row-${p.id}`}
                className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30">
                    <FileText size={18} weight="duotone" className="text-primary-hover" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-heading text-base font-semibold text-neutral-900">
                      {p.title || "Untitled"}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      /{p.slug} · Updated {formatDate(p.updated_at)}
                    </p>
                    <span
                      data-testid={`cms-pages-row-${p.id}-status`}
                      className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Link
                    to={`/cms/pages/${p.id}`}
                    data-testid={`cms-pages-row-${p.id}-edit`}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                  >
                    <Pencil size={11} weight="bold" />
                    Edit
                  </Link>
                  <button
                    type="button"
                    data-testid={`cms-pages-row-${p.id}-delete`}
                    onClick={() => onDelete(p)}
                    aria-label={`Delete ${p.title || "page"}`}
                    className="inline-flex items-center rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                  >
                    <Trash size={11} weight="bold" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" /></div>;
}

function EmptyCard({ title, desc, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <p className="font-heading text-base font-semibold text-neutral-900">{title}</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">{desc}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}
